import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FaClipboardList } from 'react-icons/fa/index.js';

import callOysterApi from '../../utils/call-oyster-api.js';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import '../../styles/UserManagementPage.css';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import ManageTeamsModal from '../../components/users/ManageTeamsModal.jsx';
import EditUserModal from '../../components/users/EditUserModal.jsx';

const UserManagementPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        page: 1,
        limit: 50,
        search: '',
        teamId: '',
        role: ''
    });
    const [teams, setTeams] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [showOnlyWithTeams, setShowOnlyWithTeams] = useState(false);

    // ... inside component
    const [isManageTeamsOpen, setIsManageTeamsOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page: filters.page,
                limit: filters.limit,
                search: filters.search,
                teamId: filters.teamId,
                role: filters.role,
                ...(showOnlyWithTeams && { hasTeam: 'true' })
            });

            const response = await callOysterApi(
                `/users/manage/list?${queryParams.toString()}`,
                'GET'
            );

            const data = await response.json().catch(() => null);
            if (response.ok && data?.success && data.payload) {
                setUsers(data.payload.users || []);
                setTotalPages(data.payload.pagination?.pages || 1);
            } else {
                const detail =
                    data?.message ||
                    `HTTP ${response.status}${
                        data?.status ? ` (body status ${data.status})` : ''
                    }`;
                console.error('[manage/list] fetch users failed:', {
                    httpStatus: response.status,
                    body: data
                });
                toast.error(`Failed to fetch users: ${detail}`);
                setUsers([]);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Error loading users');
        } finally {
            setLoading(false);
        }
    };

    const fetchTeams = async () => {
        try {
            // New endpoint /teams via proxy
            const response = await callOysterApi('/teams', 'GET');
            const data = await response.json().catch(() => null);
            if (response.ok && data?.success && Array.isArray(data.payload)) {
                setTeams(data.payload);
                if (data.payload.length === 0) {
                    console.warn(
                        '[teams] /teams returned 0 teams (teams collection may be empty/unseeded).'
                    );
                }
            } else {
                console.error('[teams] fetch teams failed:', {
                    httpStatus: response.status,
                    body: data
                });
            }
        } catch (error) {
            console.error('Error fetching teams:', error);
        }
    };

    const { loading: userLoading, user: currentUserData } = useCurrentUser();
    const showExecutivePulseColumn = !!currentUserData?.isItLeadership;

    useEffect(() => {
        if (
            userLoading ||
            !currentUserData ||
            currentUserData.isItLeadership ||
            filters.role !== 'executivePulse'
        ) {
            return;
        }
        setFilters((f) => ({ ...f, role: '', page: 1 }));
    }, [
        userLoading,
        currentUserData,
        currentUserData?.isItLeadership,
        filters.role
    ]);

    const tableColSpan = showExecutivePulseColumn ? 7 : 6;
    useEffect(() => {
        fetchTeams();
    }, []);

    // Fetch users when filters change
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            fetchUsers();
        }, 300);
        return () => clearTimeout(debounceTimer);
    }, [filters, showOnlyWithTeams]);

    // Allowed for all users now (Read-Only for regular users)
    // useEffect(() => {
    //     if (!userLoading && !canManageTeams) {
    //         toast.error('Access Denied');
    //         navigate('/');
    //     }
    // }, [userLoading, canManageTeams, navigate]);

    // Check if current user can edit the target user
    const canEditUser = (targetUser) => {
        if (userLoading || !currentUserData) return false;

        // Leadership/Admin: Can edit everyone
        if (currentUserData.isItLeadership) return true;

        // Team Lead: Can edit own team members OR users without a team
        if (currentUserData.isTeamLead) {
            const myTeam = currentUserData.hdTeam; // Assuming hdTeam holds the team name
            const targetTeam = targetUser.hdTeam;

            // If target has no team, Lead can claim/edit them?
            // Requirement: "edit users of their team or of no team"
            if (!targetTeam) return true;

            // Edit users in same team
            return myTeam && myTeam === targetTeam;
        }

        // Regular User: Cannot edit
        return false;
    };

    const getRoleBadge = (user) => {
        if (user.isItLeadership)
            return <span className="role-badge role-admin">Admin</span>;
        if (user.isTeamLead)
            return <span className="role-badge role-lead">Team Lead</span>;
        return <span className="role-badge role-user">User</span>;
    };

    const executivePulseBadge = (user) =>
        user.executivePulseAccess ? (
            <span className="role-badge" style={{ background: '#dbeafe' }}>
                Exec Pulse
            </span>
        ) : (
            '—'
        );

    return (
        <div className="user-management-container">
            <div className="user-management-header">
                <h1>User Management</h1>
                <div className="header-actions">
                    <Link
                        to="/tasks"
                        className="btn-header-outline"
                        title="Open team tasks dashboard (all teams)">
                        <FaClipboardList
                            aria-hidden
                            style={{ fontSize: '1rem', color: '#4b5563' }}
                        />
                        Team Tasks
                    </Link>
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={showOnlyWithTeams}
                            onChange={(e) =>
                                setShowOnlyWithTeams(e.target.checked)
                            }
                        />
                        Show only users with teams
                    </label>

                    {/* Manage Teams Button: Visible to all, but modal handles permissions */}
                    <button
                        className="btn-secondary"
                        onClick={() => setIsManageTeamsOpen(true)}>
                        Manage Teams
                    </button>
                </div>
            </div>

            <ManageTeamsModal
                isOpen={isManageTeamsOpen}
                onClose={() => setIsManageTeamsOpen(false)}
                onTeamsUpdated={() => {
                    fetchTeams();
                    fetchUsers();
                }}
            />

            {/* Filters */}
            <div className="user-filters-container">
                <input
                    type="text"
                    placeholder="Search name or email..."
                    className="user-search-input"
                    value={filters.search}
                    onChange={(e) =>
                        setFilters({
                            ...filters,
                            search: e.target.value,
                            page: 1
                        })
                    }
                />

                <select
                    className="user-filter-select"
                    value={filters.teamId}
                    onChange={(e) =>
                        setFilters({
                            ...filters,
                            teamId: e.target.value,
                            page: 1
                        })
                    }>
                    <option value="">All Teams</option>
                    {teams.map((team) => (
                        <option
                            key={team._id || team.name || team}
                            value={team.name || team}>
                            {team.name || team}
                        </option>
                    ))}
                </select>

                <select
                    className="user-filter-select"
                    value={filters.role}
                    onChange={(e) =>
                        setFilters({
                            ...filters,
                            role: e.target.value,
                            page: 1
                        })
                    }>
                    <option value="">All Roles</option>
                    <option value="admin">Admin</option>
                    <option value="lead">Team Lead</option>
                    <option value="user">User</option>
                    {showExecutivePulseColumn && (
                        <option value="executivePulse">Executive Pulse</option>
                    )}
                </select>
            </div>

            {/* Table */}
            <div className="users-table-container">
                <table className="users-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Team</th>
                            <th>Shift</th>
                            <th>Role</th>
                            {showExecutivePulseColumn && <th>Exec. Pulse</th>}
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={tableColSpan}>
                                    <LoadingSpinner />
                                </td>
                            </tr>
                        )}
                        {!loading && users.length === 0 && (
                            <tr>
                                <td
                                    colSpan={tableColSpan}
                                    style={{ textAlign: 'center' }}>
                                    No users found.
                                </td>
                            </tr>
                        )}
                        {!loading &&
                            users.length > 0 &&
                            users.map((user) => (
                                <tr key={user._id}>
                                    <td>
                                        {user.firstName} {user.lastName}
                                    </td>
                                    <td>{user.email}</td>
                                    <td>
                                        {user.hdTeam ? (
                                            <span className="team-badge">
                                                {user.hdTeam}
                                            </span>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td>{user.shift || '-'}</td>
                                    <td>{getRoleBadge(user)}</td>
                                    {showExecutivePulseColumn && (
                                        <td>{executivePulseBadge(user)}</td>
                                    )}
                                    <td>
                                        {canEditUser(user) && (
                                            <button
                                                className="user-edit-btn"
                                                onClick={() => {
                                                    setEditingUser(user);
                                                    setIsEditModalOpen(true);
                                                }}>
                                                Edit
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            <EditUserModal
                isOpen={isEditModalOpen}
                user={editingUser}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setEditingUser(null);
                }}
                onUserUpdated={fetchUsers}
            />

            {/* Pagination */}
            <div className="user-pagination-container">
                <div className="user-pagination-controls">
                    <button
                        className="user-page-btn"
                        disabled={filters.page <= 1}
                        onClick={() =>
                            setFilters({ ...filters, page: filters.page - 1 })
                        }>
                        Previous
                    </button>
                    <span style={{ alignSelf: 'center' }}>
                        Page {filters.page} of {totalPages}
                    </span>
                    <button
                        className="user-page-btn"
                        disabled={filters.page >= totalPages}
                        onClick={() =>
                            setFilters({ ...filters, page: filters.page + 1 })
                        }>
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserManagementPage;
